/**
 * Data layer — central export for all static/config data
 *
 * JSON configs are imported statically (Vite tree-shakes unused fields).
 * Product data is loaded async via dataService.js at runtime.
 */

import navigationContent from './navigationContent.json';
import facetConfig from './facetConfig.json';
import categoryConfig from './categoryConfig.json';
import pdpConfig from './pdpConfig.json';
import bitSelectionGuide from './bit-selection-guide.json';

// Re-export navigation tree
export const NAVIGATION = navigationContent;
export const PRODUCT_LINES = navigationContent.productLines;

/**
 * Return the customer-facing display name for a product. Fallback order:
 *   1. `descriptionShort` — SME-provided short description (rotary schema).
 *   2. `name` — friendly compact name (populated for DTH + rotary; null on
 *      most TH rows).
 *   3. `description` — the always-present long description (TH default).
 *
 * Use everywhere a product's title needs to render: PLP card titles, PDP
 * hero heading, favourites list rows, cart line items, breadcrumbs.
 *
 * @param {object} product - product record (may be null / undefined)
 * @returns {string} display name, or '' when the product has no title fields
 */
export const getProductDisplayName = (product) => {
  if (!product) return '';
  return product.descriptionShort || product.name || product.description || '';
};

/**
 * Is commerce (Add-to-cart / MiniCart / Checkout) enabled for a product line?
 *
 * Rotary products are not sold through the online cart — customers order via
 * their sales entity. The flag lives on the product-line node in
 * navigationContent.json (`commerceEnabled: false` on rotary); this helper
 * defaults to `true` for any line that doesn't set it, so TH / DTH stay
 * commerce-enabled without a config change.
 *
 * @param {string} productLine - product line id (e.g. 'top-hammer', 'rotary')
 *   or one of the aliases ('dth', 'tophammer'). Falsy → treated as enabled.
 * @returns {boolean}
 */
export const isCommerceEnabledForProductLine = (productLine) => {
  if (!productLine) return true;
  // Normalise the aliases used by the app (dth → down-the-hole,
  // tophammer → top-hammer).
  const idMap = {
    dth: 'down-the-hole',
    tophammer: 'top-hammer',
  };
  const canonical = idMap[productLine] || productLine;
  const line = navigationContent.productLines?.[canonical];
  return line?.commerceEnabled !== false;
};

// Convenience accessors
export const TOP_HAMMER = navigationContent.productLines['top-hammer'];
export const DOWN_THE_HOLE = navigationContent.productLines['down-the-hole'];

export { facetConfig as FACET_CONFIG, categoryConfig as CATEGORY_CONFIG };

// PDP configuration
export const PDP_CONFIG = pdpConfig;

// Bit selection guide (rule engine config — feeds bitSelectionService)
export const BIT_SELECTION_GUIDE = bitSelectionGuide;

// NOTE: PRODUCT_AVAILABILITY (~9 MB) is now loaded async via
// dataService.loadProductAvailability() and lives in public/data/.
// Static imports from public/ break Vite. See dataService.js.

/**
 * Resolve the PDP config branch for a given product-line id. Three-way
 * lookup: rotary / dth / top-hammer. `down-the-hole` is treated as `dth`.
 */
function getPDPLineConfig(productLine) {
  if (productLine === 'rotary') return PDP_CONFIG.rotary;
  if (productLine === 'raiseboring') return PDP_CONFIG.raiseboring;
  if (productLine === 'services') return PDP_CONFIG.services;
  if (productLine === 'dth' || productLine === 'down-the-hole') return PDP_CONFIG.dth;
  return PDP_CONFIG.topHammer;
}

/**
 * Get the section list for a PDP page based on category and product line.
 * @param {string} categorySlug - e.g. "shank-adapters", "rh460-bits", "rotary-drill-pipes"
 * @param {string} productLine  - "top-hammer" | "down-the-hole" | "rotary"
 * @returns {string[]} Array of section keys, e.g. ["overview", "specifications", "competitorXref"]
 */
export const getPDPSections = (categorySlug, productLine) => {
  const lineConfig = getPDPLineConfig(productLine);
  return lineConfig.categorySections?.[categorySlug] || lineConfig.categorySections._default;
};

/**
 * Get enriched field config objects for a category's PDP fields.
 * @param {string} categorySlug - e.g. "drill-bits", "rh460-hammers", "rotary-drill-pipes"
 * @param {string} productLine  - "top-hammer" | "dth" | "down-the-hole" | "rotary"
 * @returns {Array<{key: string, label: string, source: string, unit?: string}>}
 */
export const getPDPFieldConfig = (categorySlug, productLine) => {
  const lineConfig = getPDPLineConfig(productLine);
  const fields = lineConfig.categoryFields?.[categorySlug] || [];
  return fields.map(key => ({ key, ...lineConfig.fieldConfig[key] })).filter(f => f.label);
};

export default navigationContent;
